import { createApi } from "@/services/api.service";

export type ConfidenceSignalStatus = "enabled" | "disabled";
export type ConfidenceSignalScope = "all" | "agency" | "freelancer";
export type ScopedExpertType = "agency" | "freelancer";

/**
 * Definitions are scoped, so a single available-point total describes no real
 * expert: it counts both the agency-only and freelancer-only variant of the
 * same signal. The API reports the ceiling split by expert type instead.
 */
export type AvailablePointsByExpertType = Record<ScopedExpertType, number>;

/**
 * An alternate wording for one signal. It changes only the published text —
 * never points, source, or scope — so picking one can never move a score.
 */
export interface ConfidenceSignalLabelVariant {
  id: number;
  signal_key: string;
  /** Unique within its signal, not globally: two signals can both have `storetasker`. */
  key: string;
  label: string;
  sort_index: number;
  created_at: string;
  updated_at: string;
  /** Experts currently showing this variant. Deleting one they hold needs a cascade. */
  experts_count: number;
}

export interface ConfidenceSignalDefinition {
  key: string;
  group_key: string;
  name: string;
  public_text: string;
  points: number;
  n_factor: number | null;
  scope: ConfidenceSignalScope;
  status: ConfidenceSignalStatus;
  sort_index: number;
  /** Embedded by both list endpoints, sorted by sort_index then key. */
  label_variants?: ConfidenceSignalLabelVariant[];
}

export interface ConfidenceSignalGroup {
  key: string;
  label: string;
  sort_index: number;
  total_available_points?: AvailablePointsByExpertType;
  signals?: ConfidenceSignalDefinition[];
}

export interface ConfidenceSignalGroupsResult {
  groups: ConfidenceSignalGroup[];
  totalAvailablePoints: AvailablePointsByExpertType;
}

export interface ExpertConfidenceSignal extends ConfidenceSignalDefinition {
  has_signal: boolean;
  expert_status: ConfidenceSignalStatus | null;
  /** Always present here, including on definitions the expert does not hold. */
  label_variants: ConfidenceSignalLabelVariant[];
  /** The variant this expert shows; null means the signal's main label. */
  label_variant_key: string | null;
  /** The text this expert actually publishes — `public_text` when no variant is chosen. */
  effective_public_text: string;
}

export interface UpdateConfidenceSignalGroupInput {
  label?: string;
  sort_index?: number;
}

export interface UpdateConfidenceSignalInput {
  name?: string;
  public_text?: string;
  points?: number;
  n_factor?: number | null;
  scope?: ConfidenceSignalScope;
  status?: ConfidenceSignalStatus;
  sort_index?: number;
}

export interface CreateConfidenceSignalLabelVariantInput {
  /** Optional and immutable; the API derives it from `label` when omitted. */
  key?: string;
  label: string;
  sort_index?: number;
}

/** `key` is immutable, as it is on every other confidence-signal record. */
export interface UpdateConfidenceSignalLabelVariantInput {
  label?: string;
  sort_index?: number;
}

export interface ConfidenceSignalLabelVariantsResult {
  variants: ConfidenceSignalLabelVariant[];
  /** The signal's main label — what an expert with no variant chosen shows. */
  publicText: string;
}

/**
 * A label choice on its own leaves `origin` alone, so `status` is optional —
 * but the API rejects a body carrying neither field, so the union makes an
 * empty update a compile error rather than a 422.
 */
export type UpdateExpertConfidenceSignalInput =
  | { status: ConfidenceSignalStatus; label_variant_key?: string | null }
  /** null moves the expert back to the signal's main label. */
  | { status?: ConfidenceSignalStatus; label_variant_key: string | null };

/** Add defaults `status` server-side, so neither field is required here. */
export interface AddExpertConfidenceSignalInput {
  status?: ConfidenceSignalStatus;
  label_variant_key?: string | null;
}

const api = createApi("admin");

function endpointKey(key: string) {
  return encodeURIComponent(key);
}

/** Fresh object per call: a shared constant would let one caller's mutation leak. */
function emptyAvailablePoints(): AvailablePointsByExpertType {
  return { agency: 0, freelancer: 0 };
}

export async function listConfidenceSignalGroups(): Promise<ConfidenceSignalGroupsResult> {
  const response = await api.get<{
    success: boolean;
    data: ConfidenceSignalGroup[];
    total_available_points?: AvailablePointsByExpertType;
  }>("/confidence-signal-groups");

  return {
    groups: response.data.data,
    totalAvailablePoints: response.data.total_available_points ?? emptyAvailablePoints(),
  };
}

export async function updateConfidenceSignalGroup(
  groupKey: string,
  body: UpdateConfidenceSignalGroupInput,
): Promise<ConfidenceSignalGroup> {
  const response = await api.put<{ success: boolean; data: ConfidenceSignalGroup }>(
    `/confidence-signal-groups/${endpointKey(groupKey)}`,
    body,
  );
  return response.data.data;
}

export async function updateConfidenceSignal(
  signalKey: string,
  body: UpdateConfidenceSignalInput,
): Promise<ConfidenceSignalDefinition> {
  const response = await api.put<{ success: boolean; data: ConfidenceSignalDefinition }>(
    `/confidence-signals/${endpointKey(signalKey)}`,
    body,
  );
  return response.data.data;
}

export async function listConfidenceSignalLabelVariants(
  signalKey: string,
): Promise<ConfidenceSignalLabelVariantsResult> {
  const response = await api.get<{
    success: boolean;
    data: ConfidenceSignalLabelVariant[];
    public_text: string;
  }>(`/confidence-signals/${endpointKey(signalKey)}/label-variants`);

  return { variants: response.data.data, publicText: response.data.public_text };
}

export async function createConfidenceSignalLabelVariant(
  signalKey: string,
  body: CreateConfidenceSignalLabelVariantInput,
): Promise<ConfidenceSignalLabelVariant> {
  const response = await api.post<{ success: boolean; data: ConfidenceSignalLabelVariant }>(
    `/confidence-signals/${endpointKey(signalKey)}/label-variants`,
    body,
  );
  return response.data.data;
}

export async function updateConfidenceSignalLabelVariant(
  signalKey: string,
  variantKey: string,
  body: UpdateConfidenceSignalLabelVariantInput,
): Promise<ConfidenceSignalLabelVariant> {
  const response = await api.put<{ success: boolean; data: ConfidenceSignalLabelVariant }>(
    `/confidence-signals/${endpointKey(signalKey)}/label-variants/${endpointKey(variantKey)}`,
    body,
  );
  return response.data.data;
}

/**
 * Deleting a variant experts still show returns 409 unless `cascade` is set, so
 * nobody is silently reworded back to the main label.
 */
export async function deleteConfidenceSignalLabelVariant(
  signalKey: string,
  variantKey: string,
  cascade = false,
): Promise<void> {
  await api.delete(
    `/confidence-signals/${endpointKey(signalKey)}/label-variants/${endpointKey(variantKey)}`,
    { params: cascade ? { cascade: true } : undefined },
  );
}

export async function listExpertConfidenceSignals(
  expertId: number,
): Promise<ExpertConfidenceSignal[]> {
  const response = await api.get<{ success: boolean; data: ExpertConfidenceSignal[] }>(
    `/experts/${expertId}/confidence-signals`,
  );
  return response.data.data;
}

export async function addExpertConfidenceSignal(
  expertId: number,
  signalKey: string,
  input: AddExpertConfidenceSignalInput = {},
): Promise<void> {
  await api.post(`/experts/${expertId}/confidence-signals`, {
    signal_key: signalKey,
    status: input.status ?? "enabled",
    // Spread rather than assign: an explicit null clears the label, and an
    // absent key must stay absent rather than becoming an accidental clear.
    ...("label_variant_key" in input ? { label_variant_key: input.label_variant_key } : {}),
  });
}

/**
 * The API requires `status`, `label_variant_key`, or both, and reads an
 * explicit null as "back to the main label" — so the body is passed through
 * verbatim instead of being rebuilt from defaults.
 */
export async function updateExpertConfidenceSignal(
  expertId: number,
  signalKey: string,
  body: UpdateExpertConfidenceSignalInput,
): Promise<void> {
  await api.put(`/experts/${expertId}/confidence-signals/${endpointKey(signalKey)}`, body);
}

export async function removeExpertConfidenceSignal(
  expertId: number,
  signalKey: string,
): Promise<void> {
  await api.delete(`/experts/${expertId}/confidence-signals/${endpointKey(signalKey)}`);
}

/* -------------------------------------------------------------------------- */
/* Bonus signals                                                              */
/* -------------------------------------------------------------------------- */

export type BonusSignalSource = "preset" | "custom";

export interface BonusSignalPreset {
  key: string;
  group_key: string;
  name: string;
  public_text: string;
  points: number;
  scope: ConfidenceSignalScope;
  status: ConfidenceSignalStatus;
  sort_index: number;
  experts_count: number;
}

export interface CreateBonusSignalPresetInput {
  key?: string;
  group_key: string;
  name: string;
  public_text: string;
  points: number;
  scope?: ConfidenceSignalScope;
  status?: ConfidenceSignalStatus;
  sort_index?: number;
}

export type UpdateBonusSignalPresetInput = Partial<Omit<CreateBonusSignalPresetInput, "key">>;

export interface ListBonusSignalPresetsFilters {
  group_key?: string;
  scope?: ConfidenceSignalScope;
  status?: ConfidenceSignalStatus;
}

/** A preset as offered to one expert, already filtered to that expert's type. */
export interface ExpertBonusSignalPresetOption {
  key: string;
  group_key: string;
  name: string;
  public_text: string;
  points: number;
  scope: ConfidenceSignalScope;
  status: ConfidenceSignalStatus;
  sort_index: number;
  attached: boolean;
}

export interface ExpertBonusSignal {
  id: number;
  source: BonusSignalSource;
  preset_key: string | null;
  group_key: string;
  group_label: string | null;
  name: string | null;
  public_text: string;
  points: number;
  status: ConfidenceSignalStatus;
  sort_index: number;
  /** Whether the group ceiling left enough headroom for this bonus to score. */
  applied: boolean;
  applied_points: number;
  created_at: string;
  updated_at: string;
}

export interface AttachBonusSignalPresetInput {
  preset_key: string;
  status?: ConfidenceSignalStatus;
  sort_index?: number;
}

export interface CreateCustomBonusSignalInput {
  group_key: string;
  name?: string;
  public_text: string;
  points: number;
  status?: ConfidenceSignalStatus;
  sort_index?: number;
}

/**
 * `status` and `sort_index` are accepted on any bonus signal; the content
 * fields only on custom ones — a preset instance follows its preset.
 */
export interface UpdateExpertBonusSignalInput {
  status?: ConfidenceSignalStatus;
  sort_index?: number;
  group_key?: string;
  /** Nullable: sending null clears the internal label back to the public text. */
  name?: string | null;
  public_text?: string;
  points?: number;
}

export async function listBonusSignalPresets(
  filters: ListBonusSignalPresetsFilters = {},
): Promise<BonusSignalPreset[]> {
  const response = await api.get<{ success: boolean; data: BonusSignalPreset[] }>(
    "/bonus-signals",
    { params: filters },
  );
  return response.data.data;
}

export async function createBonusSignalPreset(
  body: CreateBonusSignalPresetInput,
): Promise<BonusSignalPreset> {
  const response = await api.post<{ success: boolean; data: BonusSignalPreset }>(
    "/bonus-signals",
    body,
  );
  return response.data.data;
}

export async function updateBonusSignalPreset(
  presetKey: string,
  body: UpdateBonusSignalPresetInput,
): Promise<BonusSignalPreset> {
  const response = await api.put<{ success: boolean; data: BonusSignalPreset }>(
    `/bonus-signals/${endpointKey(presetKey)}`,
    body,
  );
  return response.data.data;
}

/**
 * Deleting a preset experts still hold returns 409 unless `cascade` is set, so
 * points are never stripped from experts by accident.
 */
export async function deleteBonusSignalPreset(
  presetKey: string,
  cascade = false,
): Promise<void> {
  await api.delete(`/bonus-signals/${endpointKey(presetKey)}`, {
    params: cascade ? { cascade: true } : undefined,
  });
}

export async function listExpertBonusSignals(expertId: number): Promise<ExpertBonusSignal[]> {
  const response = await api.get<{ success: boolean; data: ExpertBonusSignal[] }>(
    `/experts/${expertId}/bonus-signals`,
  );
  return response.data.data;
}

export async function listExpertBonusSignalPresets(
  expertId: number,
): Promise<ExpertBonusSignalPresetOption[]> {
  const response = await api.get<{ success: boolean; data: ExpertBonusSignalPresetOption[] }>(
    `/experts/${expertId}/bonus-signals/presets`,
  );
  return response.data.data;
}

export async function addExpertBonusSignal(
  expertId: number,
  body: AttachBonusSignalPresetInput | CreateCustomBonusSignalInput,
): Promise<ExpertBonusSignal> {
  const response = await api.post<{ success: boolean; data: ExpertBonusSignal }>(
    `/experts/${expertId}/bonus-signals`,
    body,
  );
  return response.data.data;
}

export async function updateExpertBonusSignal(
  expertId: number,
  bonusSignalId: number,
  body: UpdateExpertBonusSignalInput,
): Promise<ExpertBonusSignal> {
  const response = await api.put<{ success: boolean; data: ExpertBonusSignal }>(
    `/experts/${expertId}/bonus-signals/${bonusSignalId}`,
    body,
  );
  return response.data.data;
}

export async function removeExpertBonusSignal(
  expertId: number,
  bonusSignalId: number,
): Promise<void> {
  await api.delete(`/experts/${expertId}/bonus-signals/${bonusSignalId}`);
}
